import { Fragment, useMemo } from "react";
import DOMPurify from "dompurify";
import parse, { domToReact, HTMLReactParserOptions, Element, DOMNode } from 'html-react-parser';

interface MailBodyRendererProps {
  body: string;
  /**
   * Backend-provided rich HTML (Engagement.bodyHtml) — preserves the original
   * email formatting. When present it takes precedence over `body`; the plain
   * `body` path is the fallback (preview mode, or backends without the field).
   */
  bodyHtml?: string | null;
  source?: string; // e.g., "microsoft_email", "gmail"
  mimeType?: string; // e.g., "text/html" or "text/plain"
}

/**
 * Checks if a string looks like base64 encoding
 */
const looksLikeBase64 = (str: string): boolean => {
  // Base64 strings are typically long, contain only valid base64 chars, and don't contain HTML tags
  if (str.length < 20) return false;
  if (str.includes('<') || str.includes('>')) return false;

  // Valid base64 characters (including URL-safe variants). Line breaks are
  // allowed (MIME wraps base64 at 76 chars) but not spaces: atob() skips both,
  // so punctuation-free prose like "Sounds good see you then" would otherwise
  // "decode" into garbage.
  const base64Regex = /^[A-Za-z0-9+/\-_=\r\n]+$/;
  return base64Regex.test(str);
};

/**
 * Decodes base64-encoded email body (handles URL-safe base64)
 */
export const decodeEmailBody = (base64String: string | undefined | null): string => {
  if (!base64String) return "";

  // Only attempt decode if it looks like base64
  if (!looksLikeBase64(base64String)) {
    return base64String;
  }

  try {
    // Handle URL-safe base64 (replace - with + and _ with /)
    const binaryString = atob(base64String.replace(/-/g, "+").replace(/_/g, "/"));
    const bytes = new Uint8Array([...binaryString].map(char => char.charCodeAt(0)));
    const decoder = new TextDecoder("utf-8");
    return decoder.decode(bytes);
  } catch (err) {
    // Silently return original - not actually base64
    return base64String;
  }
};

/**
 * Sanitizes Microsoft email HTML by removing unnecessary tags
 */
function sanitizeMicrosoftBody(html: string): string {
  if (!html) return "";
  return html
    .replace(/<\s*html[^>]*>/gi, "")
    .replace(/<\s*\/html\s*>/gi, "")
    .replace(/<\s*body[^>]*>/gi, "")
    .replace(/<\s*\/body\s*>/gi, "")
    .replace(/<\s*head[^>]*>[\s\S]*?<\s*\/head\s*>/gi, "");
}

/**
 * A real HTML tag (name followed by whitespace, `>`, or `/`), so tags carrying
 * attributes (`<p dir="ltr">`) count while plain-text `<name@example.com>` and
 * `<https://…>` don't. Mirrors the backend's bodyHtml sniff (coffee email_body.py).
 */
const HTML_TAG_RE = /<\/?(html|head|body|div|p|br|span|table|thead|tbody|tr|td|th|a|img|ul|ol|li|h[1-6]|strong|em|font|blockquote|pre|hr|center)[\s/>]/i;

// The link itself, without wrapping `<…>` or trailing punctuation, so plain-text
// quote headers like `Name <name@example.com>:` don't produce `mailto:<…>:`.
const URL_RE = /https?:\/\/[^\s<>"]+[^\s<>".,;:!?)'\]]/;
const EMAIL_RE = /[^\s<>()[\]"',;:]+@[^\s<>()[\]"',;:]+\.[^\s<>()[\]"',;:.]+/;

/**
 * Processes text nodes to linkify URLs and emails
 */
const processText = (text: string) => {
  const parts = text.split(/(\s+)/); // split by spaces to preserve them
  return parts.map((part, index) => {
    const url = part.match(URL_RE);
    const match = url ?? part.match(EMAIL_RE);
    if (!match || match.index === undefined) return part;
    const [hit] = match;
    return (
      <Fragment key={index}>
        {part.slice(0, match.index)}
        {url ? (
          <a href={hit} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
            {hit}
          </a>
        ) : (
          <a href={`mailto:${hit}`} className="text-blue-600 hover:underline">
            {hit}
          </a>
        )}
        {part.slice(match.index + hit.length)}
      </Fragment>
    );
  });
};

/**
 * Plain text: split into paragraphs and bullet lists for readability
 */
const PlainTextBody = ({ text }: { text: string }) => {
  const blocks = text.trim().split(/\n\s*\n/);
  return (
    <div
      className="text-sm text-foreground overflow-auto break-words space-y-3"
      style={{ fontFamily: "Arial, sans-serif", lineHeight: 1.5 }}
    >
      {blocks.map((block, bi) => {
        const lines = block.split(/\n/);
        const bulletLines = lines.filter((l) => /^\s*[-*•]\s+/.test(l));
        const numberedLines = lines.filter((l) => /^\s*\d+[.)]\s+/.test(l));
        if (bulletLines.length === lines.length && lines.length > 1) {
          return (
            <ul key={bi} className="list-disc pl-5 space-y-1">
              {lines.map((l, i) => (
                <li key={i}>{processText(l.replace(/^\s*[-*•]\s+/, ""))}</li>
              ))}
            </ul>
          );
        }
        if (numberedLines.length === lines.length && lines.length > 1) {
          return (
            <ol key={bi} className="list-decimal pl-5 space-y-1">
              {lines.map((l, i) => (
                <li key={i}>{processText(l.replace(/^\s*\d+[.)]\s+/, ""))}</li>
              ))}
            </ol>
          );
        }
        return (
          <p key={bi} className="whitespace-pre-line">
            {processText(block)}
          </p>
        );
      })}
    </div>
  );
};

/**
 * Renders email body with proper HTML sanitization and decoding
 * Supports plain text and HTML emails, with base64 decoding
 */
const MailBodyRenderer: React.FC<MailBodyRendererProps> = ({
  body,
  bodyHtml,
  source,
  mimeType = "text/html",
}) => {
  const decodedBody = useMemo(() => {
    // Try to decode if it looks like base64 (function handles errors gracefully)
    const decoded = decodeEmailBody(body) || body || "";

    // Debug logging for whitespace issues
    if (decoded && decoded.length > 0) {
      const firstChars = decoded.substring(0, 50);
      const hasLeadingWhitespace = /^\s+/.test(decoded);
      if (hasLeadingWhitespace) {
        console.log('[MailBodyRenderer] Leading whitespace detected:', {
          source,
          mimeType,
          firstChars: JSON.stringify(firstChars),
          leadingWhitespaceCount: decoded.match(/^\s+/)?.[0].length,
        });
      }
    }

    return decoded;
  }, [body, source, mimeType]);

  // HTML React Parser options to linkify text
  const options: HTMLReactParserOptions = {
    replace: (domNode: DOMNode) => {
      if (domNode.type === 'text' && 'data' in domNode) {
        const text = (domNode as { data: string }).data || '';
        return <>{processText(text)}</>;
      }
      return undefined;
    },
  };

  // Preferred path: backend-provided rich HTML (Engagement.bodyHtml). The
  // server already removed non-content tags; DOMPurify is the authoritative
  // XSS boundary before we render. Falls through to the body-based paths below
  // when absent (preview mode, or a backend without the field). A plain-text
  // email comes back as its text; rendering that as HTML would collapse its
  // line breaks, so it takes the plain-text path (no base64 step: the backend
  // already decoded it).
  if (bodyHtml && bodyHtml.trim()) {
    if (!HTML_TAG_RE.test(bodyHtml) && !/<!doctype/i.test(bodyHtml)) {
      return <PlainTextBody text={bodyHtml} />;
    }
    const sanitized = DOMPurify.sanitize(bodyHtml, { USE_PROFILES: { html: true } });
    return (
      <div
        className="prose prose-sm max-w-none text-sm text-foreground overflow-auto break-words [&_a]:text-primary [&_a]:underline"
        style={{ fontFamily: "Arial, sans-serif", lineHeight: 1.5 }}
      >
        {parse(sanitized, options)}
      </div>
    );
  }

  // Microsoft email: strip extra tags before parsing.
  // Use decodedBody (base64-decoded) rather than the raw body, otherwise a
  // base64-encoded Microsoft body is parsed as gibberish.
  if (source === "microsoft_email") {
    const sanitizedHtml = sanitizeMicrosoftBody(decodedBody).trim();

    return (
      <div
        className="prose prose-sm max-w-none text-sm text-foreground overflow-auto break-words [&_a]:text-primary [&_a]:underline"
        style={{
          fontFamily: "Arial, sans-serif",
          lineHeight: 1.5,
        }}
      >
        {parse(sanitizedHtml, options)}
      </div>
    );
  }

  // HTML email: detect HTML and parse (html-react-parser handles sanitization).
  // Sniff for a tag name followed by whitespace, `>`, or `/` so tags carrying
  // attributes (e.g. `<p dir="ltr">`, `<p style="...">`) are still recognized —
  // matching only bare `<p>` misroutes real HTML emails to the plain-text branch.
  const isHTML = mimeType === "text/html" ||
    /<!doctype/i.test(decodedBody) ||
    HTML_TAG_RE.test(decodedBody);

  if (isHTML) {
    // Trim leading/trailing whitespace from HTML content
    const trimmedHtml = decodedBody.trim();

    return (
      <div
        className="prose prose-sm max-w-none text-sm text-foreground overflow-auto break-words [&_a]:text-primary [&_a]:underline"
        style={{
          fontFamily: "Arial, sans-serif",
          lineHeight: 1.5,
        }}
      >
        {parse(trimmedHtml, options)}
      </div>
    );
  }

  return <PlainTextBody text={decodedBody} />;
};

export default MailBodyRenderer;
